import React, { PureComponent, ReactNode } from "react";
import { MusicNoteIdentity, MusicNoteSimpleWeight, MusicScale, NoteScaleOverride } from "../../../wasm/pkg/wasm";
import { AppContextType, MenuName } from "../../AppContext";
// import { ICON_NOTEOVERRIDE } from "../../constants/iconConstants";
import { listAllNoteIds, NoteSettingsDecision, updateNoteSettingsWithManualOverride } from "../../models/wasm.model";
import { emptyNoteScaleMeta, NoteScaleMeta, updateNoteMetaWithLatestScale } from "../../statemachines/NoteScaleMetaState";
import InfoCard from "../layout_reuse/InfoCard";
import MusicNote from "../music_visuals/history_monitor/MusicNote";
import IconButton from "../theme_reuse/IconButton";
import ThreeOptionSlider from "../theme_reuse/ThreeOptionSlider";
import "./NoteValidityControls.css";
import KeyMajorMinorButton from "./custom_buttons/KeyMajorMinorButton";
import OpenSettingsMenuButton from "./custom_buttons/OpenSettingsMenuButton";
import { ICON_REFRESH } from "../../constants/iconConstants";

interface Props {
  scale: MusicScale | undefined;
  totalNoteWeights: { [noteId: string]: MusicNoteSimpleWeight };
  noteSettings: Array<NoteSettingsDecision>;
  // FIXME: rename something about note settings
  onChangeNoteSettings: (newSettings: Array<NoteSettingsDecision>) => void;
}
interface State {
  sortedNotes: Array<NoteScaleMeta>;
  sortedIndexOfC: number;
  showControls: boolean;
  noteSettingsMap: { [key: string]: NoteSettingsDecision };
}

const SLIDER_CHOICES: Array<NoteScaleOverride> = ["None", "InScale", "OutOfScale", "Accidental"];

const resetSortedNotes = () => listAllNoteIds().map((id) => emptyNoteScaleMeta(id as MusicNoteIdentity));

/**
 * Things you might want to toggle:
 *  - ignore this note, it's not valid
 *  - this note appears as an accidental but isn't part of the scale
 *  - ??
 *
 * ...-3 mode toggle?  Radio buttons styled as a switch
 */
export default class NoteValidityControls extends PureComponent<Props, State> {
  declare context: React.ContextType<typeof AppContextType>;
  constructor(props: Props) {
    super(props);

    this.state = {
      sortedNotes: resetSortedNotes(),
      sortedIndexOfC: 3,
      /* This currently isn't stateful.  The button to hide it felt unnecessary. */
      showControls: true,
      noteSettingsMap: {},
    };
  }

  componentDidUpdate(oldProps: Props) {
    // FIXME: when the major/minor toggle is added, update the sorting (and scale degrees)
    const oldTonic = oldProps.scale?.family.minor_tonic_id || "A";
    const newTonic = this.props.scale?.family.minor_tonic_id || "A";
    const oldScale = oldTonic + oldProps.scale?.shape;
    const newScale = newTonic + this.props.scale?.shape;
    if (!this.props.scale) {
      // only do this once.  But do skip the other conditions if no scale.
      if (oldProps.scale) {
        this.setState({ sortedNotes: resetSortedNotes() });
      }
    } else if (oldTonic !== newTonic) {
      // Apply scale information to the sortedNotes for simpler render func.
      let sortedNotes = this.state.sortedNotes;
      // FIXME: rotation is annoying/jumpy for the user.  Perhaps make it a UI setting.
      // sortedNotes = rotateSortedNotesToBeginWithTonic(this.state.sortedNotes, newTonic);
      const sortedIndexOfC = sortedNotes.findIndex((sn) => sn.id === "C");
      sortedNotes = updateNoteMetaWithLatestScale(sortedNotes, this.props.scale!);
      this.setState({ sortedNotes, sortedIndexOfC });
    } else if (oldScale !== newScale) {
      // i.e. the scale shape changed, the tonic remains the same
      const sortedNotes = updateNoteMetaWithLatestScale(this.state.sortedNotes, this.props.scale!);
      this.setState({ sortedNotes });
    }

    // Convert UserSettings from array to map for simpler render func.
    if (oldProps.noteSettings !== this.props.noteSettings) {
      const noteSettingsMap: { [key: string]: NoteSettingsDecision } = {};
      this.props.noteSettings.forEach((ns) => {
        noteSettingsMap[ns.settings.note_id as string] = ns;
      });
      this.setState({ noteSettingsMap });
    }
  }

  handleSliderChange = (note_id: string, sliderOption: 0 | 1 | 2 | 3) => {
    const noteSettings = this.props.noteSettings;
    const choice = SLIDER_CHOICES[sliderOption];
    const newSettings = updateNoteSettingsWithManualOverride(noteSettings, note_id as MusicNoteIdentity, choice);
    this.props.onChangeNoteSettings(newSettings);
  };

  // togglePinOverridesMode = () => {
  //   this.setState({ showControls: !this.state.showControls });
  // };

  chooseNoteOpacityStyle = (noteId: MusicNoteIdentity) => {
    const weight = this.props.totalNoteWeights[noteId as string]?.weight;
    if (weight) {
      return { background: `rgba(1, 1, 1, ${weight})` };
    }
    return {};
  };

  handleResetPins = () => {
    this.props.onChangeNoteSettings([]);
  };

  render(): ReactNode {
    const { sortedNotes, sortedIndexOfC, showControls, noteSettingsMap } = this.state;
    const { scale, totalNoteWeights } = this.props;
    const hasWeights = Object.keys(totalNoteWeights).length > 0;
    const hasSettings = Object.keys(noteSettingsMap).length > 0;

    return (
      <InfoCard includesHeaderBar className="area-note-validity">
        <div className="info-card-header-bar">
          <div className="note-validity-header-bar">
            {!hasWeights && hasSettings && <IconButton mini icon={ICON_REFRESH} title="Clear All Pins" onClick={this.handleResetPins} />}
            <KeyMajorMinorButton />
            {/* <IconButton mini title="Manually Override Note Inclusion" icon={ICON_NOTEOVERRIDE} onClick={this.togglePinOverridesMode} /> */}
            <OpenSettingsMenuButton menuName={MenuName.KeyIdOptions} />
          </div>
        </div>
        <div className="info-card-body">
          <div className="note-validity-controls-grid">
            {sortedNotes.map((sn, index) => {
              const soundOctave = index >= sortedIndexOfC ? "Four" : "Three";
              const { isInScaleShape, id: nid } = sn; // isInScaleFamily - unsure how to UX
              const { scale_override } = noteSettingsMap[sn.id]?.settings || {};
              const checkedOpt = chooseCheckedOption(Boolean(scale), isInScaleShape, scale_override);
              const isPin = scale_override && scale_override !== "None";
              const isPinManual = isPin && noteSettingsMap[sn.id].why?.includes("manual");
              const style = this.chooseNoteOpacityStyle(sn.id);
              return (
                <div key={"validity" + nid} className="note-validity-single-note" style={style}>
                  <MusicNote note_id={nid} scale={scale} octave={soundOctave} hideOctave strikethroughWrongNotes />
                  {showControls && (
                    <ThreeOptionSlider
                      isPin={isPin}
                      isPinManual={isPinManual}
                      checkedOpt={checkedOpt}
                      onChangeOption={this.handleSliderChange}
                      sliderId={nid}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </InfoCard>
    );
  }
}

NoteValidityControls.contextType = AppContextType;

function chooseCheckedOption(
  scaleExists: boolean,
  isInScaleShape: boolean,
  noteScaleOverride: NoteScaleOverride | undefined
): 0 | 1 | 2 | 3 {
  if (noteScaleOverride) {
    return SLIDER_CHOICES.findIndex((sc) => sc === noteScaleOverride) as 0 | 1 | 2 | 3;
  } else if (!scaleExists) {
    return 0;
  } else {
    return isInScaleShape ? 1 : 2;
  }
}
